// Canonical Gao SIWE (EIP-4361) message construction.
//
// The console builds the same standard message every other Gao product builds,
// and `gao-id-worker` parses it with viem's `parseSiweMessage` in `lib/siwe.ts`.
// Admin used to sign a bespoke EIP-712 `AdminLogin` object instead; that is
// retired, and this file is what replaces it.
//
// ─── Why this is hand-built rather than an npm SIWE library ───────────────
//
// The console ships with three runtime dependencies — react, react-dom and
// react-router-dom — and §6's bundle scan is easier to trust the fewer there
// are. `gao-workspace-web/src/gaoid/siwe-message.ts` is the existing Gao
// precedent for exactly this: a dependency-free builder, byte-compatible with
// viem's `createSiweMessage`, pinned by tests. This follows it deliberately
// rather than inventing a second format.
//
// ─── Why domain and uri come from config, never from the page ─────────────
//
// They are the two fields the issuer matches against SIWE_DOMAIN and
// ALLOWED_ORIGINS. Deriving them from `window.location` would let a framed,
// proxied or wrapped page produce a message for an origin this deployment never
// intended to authorize. Reading them from validated configuration means they
// are always exactly the allow-listed pair.

import { ADMIN_SIWE_DOMAIN, ADMIN_SIWE_URI } from "../config";

/** Shown to the operator in the wallet. Wording only; it is not authority. */
export const SIWE_STATEMENT = "Sign in to Gao Admin.";
export const SIWE_VERSION = "1";

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
/** gao-id-worker mints nonces from [A-Za-z0-9] only (src/lib/siwe.ts). */
const NONCE_RE = /^[A-Za-z0-9]{8,64}$/;

export class SiweMessageError extends Error {
  constructor(
    public readonly field: string,
    message: string,
  ) {
    super(message);
    this.name = "SiweMessageError";
  }
}

export interface BuildSiweMessageInput {
  address: string;
  /** The chain the WALLET is on. Protocol metadata and replay binding — it does
   *  not choose an operator and it is not the GaoSafe authority chain, which
   *  lives server-side and is never sent from here. */
  chainId: number;
  nonce: string;
  /** Injected so the message is deterministic under test. */
  issuedAt: Date;
}

export function buildSiweMessage(input: BuildSiweMessageInput): string {
  if (typeof input.address !== "string" || !ADDRESS_RE.test(input.address.trim())) {
    throw new SiweMessageError("address", "address must be a 0x-prefixed 20-byte hex string");
  }
  if (
    typeof input.chainId !== "number" ||
    !Number.isInteger(input.chainId) ||
    input.chainId <= 0
  ) {
    throw new SiweMessageError("chainId", "chainId must be a positive integer");
  }
  if (typeof input.nonce !== "string" || !NONCE_RE.test(input.nonce)) {
    throw new SiweMessageError("nonce", "nonce must be 8-64 alphanumeric characters");
  }
  if (Number.isNaN(input.issuedAt.getTime())) {
    throw new SiweMessageError("issuedAt", "issuedAt must be a valid date");
  }

  return [
    `${ADMIN_SIWE_DOMAIN} wants you to sign in with your Ethereum account:`,
    input.address.trim(),
    "",
    SIWE_STATEMENT,
    "",
    `URI: ${ADMIN_SIWE_URI}`,
    `Version: ${SIWE_VERSION}`,
    `Chain ID: ${input.chainId}`,
    `Nonce: ${input.nonce}`,
    `Issued At: ${input.issuedAt.toISOString()}`,
  ].join("\n");
}
